import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Settings as SettingsIcon,
  Building2,
  FolderKanban,
  CheckSquare,
  Users,
  DollarSign,
  Eye,
  Save,
  ArrowLeft,
  Database,
  Download,
  RefreshCw,
  Trash2,
  Shield,
  Languages,
} from 'lucide-react';

import { Button } from '../components/ui/button';
import { Organization, Project, User, UserRole } from '../types';
import { importACSProject } from '../utils/importACSProject';
import { toast } from 'sonner';
import { useApp } from '../providers/AppProvider';
import { updateOrganization } from '../services/dataService';
import {
  AppLanguage,
  I18N_CONTROL_TERM_KEYS,
  usePreferences,
} from '../contexts/PreferencesContext';
import { APP_NATIVE_FIELD_CLASS } from '../constants/formControls';
import { OrganizationColorScheme, OrganizationThemeDefault, ORG_COLOR_PALETTES } from '../constants/organizationTheme';
import {
  WIDGET_MUTED_TEXT_CLASS,
  WIDGET_PANEL_CLASS,
  WIDGET_SUBTLE_PANEL_CLASS,
} from '../constants/widgetTheme';

interface SettingsData {
  organizations: Organization[];
  projects: Project[];
  users: User[];
  rolePermissions: Record<UserRole, RolePermissions>;
  taskSettings: TaskSettings;
}

interface RolePermissions {
  canViewCosts: boolean;
  canEditCosts: boolean;
  canViewAllProjects: boolean;
  canManageUsers: boolean;
  canManageSettings: boolean;
}

interface TaskSettings {
  requireEstimates: boolean;
  requireAssignee: boolean;
  allowMultipleAssignees: boolean;
  enableTimeTracking: boolean;
  enableStandupNotes: boolean;
  enableRatings: boolean;
  enableAttachments: boolean;
}

const LANGUAGE_OPTIONS: Array<{ value: AppLanguage; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'bn', label: 'Bengali' },
];

const TERM_LABELS: Record<string, string> = {
  dailyStandup: 'Daily standup',
  logHours: 'Log hours',
  selectTask: 'Select task',
  relatedTaskOptional: 'Related task (optional)',
  whatDidYouAccomplishToday: 'What did you accomplish today?',
  submitStandupNote: 'Submit standup note',
  hoursWorked: 'Hours worked',
  saveChanges: 'Save changes',
  cancel: 'Cancel',
  today: 'Today',
  calendar: 'Calendar',
  meetings: 'Meetings',
};

const SETTINGS_SECTION_CLASS = `${WIDGET_PANEL_CLASS} p-6`;
const SETTINGS_SUBSECTION_CLASS = `${WIDGET_SUBTLE_PANEL_CLASS} p-4`;

export default function Settings() {
  const navigate = useNavigate();
  const { state } = useApp();
  const { preferences, setLanguage, getLanguageTerms, setTranslationTerm, getTranslation } = usePreferences();
  const [activeTab, setActiveTab] = useState<'organizations' | 'projects' | 'tasks' | 'stakeholders' | 'data' | 'language'>('organizations');
  const [saveMessage, setSaveMessage] = useState('');
  const [orgThemeSettings, setOrgThemeSettings] = useState<Record<string, { colorScheme: OrganizationColorScheme; defaultTheme: OrganizationThemeDefault }>>({});

  const languageTerms = useMemo(() => getLanguageTerms(), [getLanguageTerms, preferences.language, preferences.customTranslations]);

  // Settings defaults (role/task toggles are local; org/project/user data syncs from app state)
  const [settings, setSettings] = useState<SettingsData>({
    organizations: [],
    projects: [],
    users: [],
    rolePermissions: {
      developer: {
        canViewCosts: false,
        canEditCosts: false,
        canViewAllProjects: false,
        canManageUsers: false,
        canManageSettings: false,
      },
      stakeholder: {
        canViewCosts: true,
        canEditCosts: false,
        canViewAllProjects: true,
        canManageUsers: false,
        canManageSettings: false,
      },
      investor: {
        canViewCosts: true,
        canEditCosts: false,
        canViewAllProjects: true,
        canManageUsers: false,
        canManageSettings: false,
      },
      hr: {
        canViewCosts: false,
        canEditCosts: false,
        canViewAllProjects: true,
        canManageUsers: true,
        canManageSettings: false,
      },
      external_partner: {
        canViewCosts: false,
        canEditCosts: false,
        canViewAllProjects: false,
        canManageUsers: false,
        canManageSettings: false,
      },
      admin: {
        canViewCosts: true,
        canEditCosts: true,
        canViewAllProjects: true,
        canManageUsers: true,
        canManageSettings: true,
      },
    },
    taskSettings: {
      requireEstimates: true,
      requireAssignee: true,
      allowMultipleAssignees: true,
      enableTimeTracking: true,
      enableStandupNotes: true,
      enableRatings: true,
      enableAttachments: true,
    }
  });

  useEffect(() => {
    if (!state) {
      return;
    }

    setSettings((prev) => ({
      ...prev,
      organizations: Array.isArray(state.organizations) ? state.organizations : prev.organizations,
      projects: Array.isArray(state.projects) ? state.projects : prev.projects,
      users: Array.isArray(state.users) ? state.users : prev.users,
    }));

    // Initialize org theme settings
    if (Array.isArray(state.organizations)) {
      const themeSettings = state.organizations.reduce((acc, org) => {
        acc[org.id] = {
          colorScheme: (org.settings?.appearance?.colorScheme || 'default') as OrganizationColorScheme,
          defaultTheme: (org.settings?.appearance?.defaultTheme || 'light') as OrganizationThemeDefault,
        };
        return acc;
      }, {} as Record<string, { colorScheme: OrganizationColorScheme; defaultTheme: OrganizationThemeDefault }>);
      setOrgThemeSettings(themeSettings);
    }
  }, [state]);

  const getProjectBudgetTotal = (project: Project): number => {
    return (project.costs || []).reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  };

  const handleRolePermissionChange = (role: UserRole, permission: keyof RolePermissions) => {
    setSettings(prev => ({
      ...prev,
      rolePermissions: {
        ...prev.rolePermissions,
        [role]: {
          ...prev.rolePermissions[role],
          [permission]: !prev.rolePermissions[role][permission]
        }
      }
    }));
  };

  const handleTaskSettingChange = (setting: keyof TaskSettings) => {
    setSettings(prev => ({
      ...prev,
      taskSettings: {
        ...prev.taskSettings,
        [setting]: !prev.taskSettings[setting]
      }
    }));
  };

  const handleSave = () => {
    // Save to backend/context
    setSaveMessage('Settings saved successfully!');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const handleOrgThemeSave = async (orgId: string) => {
    try {
      const theme = orgThemeSettings[orgId];
      if (!theme) return;

      await updateOrganization(orgId, {
        settings: {
          appearance: {
            colorScheme: theme.colorScheme,
            defaultTheme: theme.defaultTheme,
          },
        },
      });
      toast.success('Organization theme updated successfully!');
    } catch (error) {
      toast.error('Failed to save organization theme');
      console.error('Error saving org theme:', error);
    }
  };

  const handleOrgColorSchemeChange = (orgId: string, colorScheme: OrganizationColorScheme) => {
    setOrgThemeSettings((prev) => ({
      ...prev,
      [orgId]: {
        ...prev[orgId],
        colorScheme,
      },
    }));
  };

  const handleOrgDefaultThemeChange = (orgId: string, defaultTheme: OrganizationThemeDefault) => {
    setOrgThemeSettings((prev) => ({
      ...prev,
      [orgId]: {
        ...prev[orgId],
        defaultTheme,
      },
    }));
  };

  const handleImportACSProject = async (file: File) => {
    try {
      const project = await importACSProject(file);
      setSettings(prev => ({
        ...prev,
        projects: [...prev.projects, project]
      }));
      toast.success('Project imported successfully!');
    } catch (error) {
      toast.error('Failed to import project. Please check the file format.');
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 pb-6 pt-3 text-foreground sm:px-6 lg:px-8 lg:pt-4">
      <div className="mb-4 sm:mb-5">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <SettingsIcon className="h-5 w-5 text-primary" />
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">Settings</h1>
              <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
                Manage organizations, projects, permissions, data, and localization.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/app')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button onClick={handleSave}>
              <Save className="mr-2 h-4 w-4" />
              {getTranslation('saveChanges', 'Save Changes')}
            </Button>
          </div>
        </div>
        {saveMessage && (
          <div className="mt-3 rounded-lg border border-border bg-muted p-3 text-sm text-foreground">
            {saveMessage}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4 lg:gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-card rounded-lg shadow-sm border border-border p-4 space-y-2">
              <button
                onClick={() => setActiveTab('organizations')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === 'organizations' ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
                }`}
              >
                <Building2 className="w-5 h-5" />
                <span className="font-medium">Organizations</span>
              </button>
              <button
                onClick={() => setActiveTab('projects')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === 'projects' ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
                }`}
              >
                <FolderKanban className="w-5 h-5" />
                <span className="font-medium">Projects</span>
              </button>
              <button
                onClick={() => setActiveTab('tasks')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === 'tasks' ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
                }`}
              >
                <CheckSquare className="w-5 h-5" />
                <span className="font-medium">Tasks</span>
              </button>
              <button
                onClick={() => setActiveTab('stakeholders')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === 'stakeholders' ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
                }`}
              >
                <Users className="w-5 h-5" />
                <span className="font-medium">Stakeholders & Roles</span>
              </button>
              <button
                onClick={() => setActiveTab('data')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === 'data' ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
                }`}
              >
                <Database className="w-5 h-5" />
                <span className="font-medium">Data Management</span>
              </button>
              <button
                onClick={() => setActiveTab('language')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === 'language' ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
                }`}
              >
                <Languages className="w-5 h-5" />
                <span className="font-medium">Language & i18n</span>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            {activeTab === 'organizations' && (
              <div className={SETTINGS_SECTION_CLASS}>
                <h2 className="text-xl font-bold mb-6">Organization Settings</h2>
                <div className="space-y-6">
                  {settings.organizations.map((org) => (
                    <div key={org.id} className={SETTINGS_SUBSECTION_CLASS}>
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-semibold text-lg">{org.name}</h3>
                          <p className="text-sm text-muted-foreground">{org.industry}</p>
                          <p className="text-xs text-muted-foreground mt-1">Created: {new Date(org.createdAt).toLocaleDateString()}</p>
                        </div>
                        <Button variant="outline" size="sm">Edit</Button>
                      </div>

                      {/* Theme Settings */}
                      <div className={SETTINGS_SUBSECTION_CLASS}>
                        <h4 className="font-semibold text-sm mb-3">Organization Theme</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-2">
                              Color Scheme
                            </label>
                            <select
                              value={orgThemeSettings[org.id]?.colorScheme || 'default'}
                              onChange={(e) => handleOrgColorSchemeChange(org.id, e.target.value as OrganizationColorScheme)}
                              className={APP_NATIVE_FIELD_CLASS}
                              title="Organization color scheme"
                              aria-label="Organization color scheme"
                            >
                              <option value="default">Default</option>
                              <option value="blue">Blue</option>
                              <option value="emerald">Emerald</option>
                              <option value="amber">Amber</option>
                              <option value="rose">Rose</option>
                              <option value="slate">Slate</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-2">
                              Default Theme
                            </label>
                            <select
                              value={orgThemeSettings[org.id]?.defaultTheme || 'light'}
                              onChange={(e) => handleOrgDefaultThemeChange(org.id, e.target.value as OrganizationThemeDefault)}
                              className={APP_NATIVE_FIELD_CLASS}
                              title="Default theme for organization"
                              aria-label="Default theme for organization"
                            >
                              <option value="light">Light</option>
                              <option value="dark">Dark</option>
                              <option value="system">System</option>
                            </select>
                          </div>
                        </div>
                        <button
                          onClick={() => handleOrgThemeSave(org.id)}
                          className="mt-3 px-3 py-1.5 bg-ring text-white rounded text-sm hover:bg-ring/90"
                        >
                          Save Theme
                        </button>
                      </div>

                      {/* Other Settings */}
                      <div className="space-y-3">
                        <div className={`${WIDGET_SUBTLE_PANEL_CLASS} flex items-center justify-between p-3`}>
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">Enable cost tracking</span>
                          </div>
                          <input
                            type="checkbox"
                            defaultChecked
                            className="w-4 h-4"
                            title="Enable cost tracking"
                            aria-label="Enable cost tracking"
                          />
                        </div>
                        <div className={`${WIDGET_SUBTLE_PANEL_CLASS} flex items-center justify-between p-3`}>
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">Allow external partners</span>
                          </div>
                          <input
                            type="checkbox"
                            defaultChecked
                            className="w-4 h-4"
                            title="Allow external partners"
                            aria-label="Allow external partners"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'projects' && (
              <div className={SETTINGS_SECTION_CLASS}>
                <h2 className="text-xl font-bold mb-6">Project Settings</h2>
                <div className="space-y-6">
                  {settings.projects.map((project) => (
                    <div key={project.id} className={SETTINGS_SUBSECTION_CLASS}>
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-semibold text-lg">{project.name}</h3>
                          <p className={`text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>{project.description}</p>
                          <div className={`mt-2 flex gap-4 text-xs ${WIDGET_MUTED_TEXT_CLASS}`}>
                            <span>Cost Plan: ${getProjectBudgetTotal(project).toLocaleString()}</span>
                            <span>Status: {project.status}</span>
                          </div>
                        </div>
                        <Button variant="outline" size="sm">Edit</Button>
                      </div>
                      <div className="space-y-3">
                        <div className={`${WIDGET_SUBTLE_PANEL_CLASS} flex items-center justify-between p-3`}>
                          <div className="flex items-center gap-2">
                            <DollarSign className={`w-4 h-4 ${WIDGET_MUTED_TEXT_CLASS}`} />
                            <span className="text-sm">Show costs to all team members</span>
                          </div>
                          <input
                            type="checkbox"
                            className="w-4 h-4"
                            title="Show costs to all team members"
                            aria-label="Show costs to all team members"
                          />
                        </div>
                        <div className={`${WIDGET_SUBTLE_PANEL_CLASS} flex items-center justify-between p-3`}>
                          <div className="flex items-center gap-2">
                            <Eye className={`w-4 h-4 ${WIDGET_MUTED_TEXT_CLASS}`} />
                            <span className="text-sm">Visible to external partners</span>
                          </div>
                          <input
                            type="checkbox"
                            className="w-4 h-4"
                            title="Visible to external partners"
                            aria-label="Visible to external partners"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6">
                  <label className={`block text-sm font-medium ${WIDGET_MUTED_TEXT_CLASS}`}>Import ACS Project</label>
                  <div className="mt-1 flex items-center">
                    <input
                      type="file"
                      accept=".acs"
                      title="Import ACS project file"
                      aria-label="Import ACS project file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleImportACSProject(file);
                        }
                      }}
                      className="sr-only"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="relative"
                    >
                      <Database className="w-4 h-4 mr-2" />
                      Import
                      <input
                        type="file"
                        accept=".acs"
                        title="Upload ACS file"
                        aria-label="Upload ACS file"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleImportACSProject(file);
                          }
                        }}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                      />
                    </Button>
                  </div>
                </div>
                
                {/* Import Sample Data Section */}
                <div className="mt-8 p-6 border-2 border-dashed border-primary/40 rounded-lg bg-primary/10">
                  <div className="flex items-start gap-4">
                    <Database className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2">Import ACS Sample Project</h3>
                      <p className="mb-4 text-sm text-foreground/85">
                        Import the completed ACS (Accounts.AI) project from Intellesphere with 11 epics, 50+ tasks, 
                        and the new P1 phase for automated anomaly analysis using LLM.
                      </p>
                      <Button
                        onClick={() => {
                          const result = importACSProject();
                          if (result.success) {
                            toast.success(`✅ ${result.message}`, {
                              description: `Added ${result.stats.epics} epics and ${result.stats.tasks} tasks`,
                            });
                            setTimeout(() => navigate('/app/projects'), 1500);
                          } else {
                            toast.error(result.message);
                          }
                        }}
                        className="bg-primary/10 hover:bg-primary/10"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Import ACS Project Data
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'tasks' && (
              <div className={SETTINGS_SECTION_CLASS}>
                <h2 className="text-xl font-bold mb-6">Task Settings</h2>
                <div className="space-y-4">
                  <SettingToggle
                    label="Require time estimates"
                    description="Tasks must have estimated days before assignment"
                    checked={settings.taskSettings.requireEstimates}
                    onChange={() => handleTaskSettingChange('requireEstimates')}
                  />
                  <SettingToggle
                    label="Require assignee"
                    description="Tasks must be assigned to at least one person"
                    checked={settings.taskSettings.requireAssignee}
                    onChange={() => handleTaskSettingChange('requireAssignee')}
                  />
                  <SettingToggle
                    label="Allow multiple assignees"
                    description="Tasks can be assigned to multiple team members"
                    checked={settings.taskSettings.allowMultipleAssignees}
                    onChange={() => handleTaskSettingChange('allowMultipleAssignees')}
                  />
                  <SettingToggle
                    label="Enable time tracking"
                    description="Team members can log hours worked on tasks"
                    checked={settings.taskSettings.enableTimeTracking}
                    onChange={() => handleTaskSettingChange('enableTimeTracking')}
                  />
                  <SettingToggle
                    label="Enable standup notes"
                    description="Team members can add daily standup updates"
                    checked={settings.taskSettings.enableStandupNotes}
                    onChange={() => handleTaskSettingChange('enableStandupNotes')}
                  />
                  <SettingToggle
                    label="Enable task ratings"
                    description="Completed tasks can be rated for quality"
                    checked={settings.taskSettings.enableRatings}
                    onChange={() => handleTaskSettingChange('enableRatings')}
                  />
                  <SettingToggle
                    label="Enable attachments"
                    description="Allow file attachments on tasks"
                    checked={settings.taskSettings.enableAttachments}
                    onChange={() => handleTaskSettingChange('enableAttachments')}
                  />
                </div>
              </div>
            )}

            {activeTab === 'stakeholders' && (
              <div className={SETTINGS_SECTION_CLASS}>
                <h2 className="text-xl font-bold mb-6">Role Permissions</h2>
                <p className={`mb-6 text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>
                  Configure what each role can see and do in the system
                </p>
                <div className="space-y-6">
                  {Object.entries(settings.rolePermissions).map(([role, permissions]) => (
                    <div key={role} className={SETTINGS_SUBSECTION_CLASS}>
                      <h3 className="font-semibold text-lg mb-4 capitalize">{role.replace('_', ' ')}</h3>
                      <div className="space-y-3">
                        <PermissionToggle
                          icon={<DollarSign className="w-4 h-4" />}
                          label="View costs and budgets"
                          checked={permissions.canViewCosts}
                          onChange={() => handleRolePermissionChange(role as UserRole, 'canViewCosts')}
                        />
                        <PermissionToggle
                          icon={<DollarSign className="w-4 h-4" />}
                          label="Edit costs and budgets"
                          checked={permissions.canEditCosts}
                          onChange={() => handleRolePermissionChange(role as UserRole, 'canEditCosts')}
                        />
                        <PermissionToggle
                          icon={<Eye className="w-4 h-4" />}
                          label="View all projects"
                          checked={permissions.canViewAllProjects}
                          onChange={() => handleRolePermissionChange(role as UserRole, 'canViewAllProjects')}
                        />
                        <PermissionToggle
                          icon={<Users className="w-4 h-4" />}
                          label="Manage users"
                          checked={permissions.canManageUsers}
                          onChange={() => handleRolePermissionChange(role as UserRole, 'canManageUsers')}
                        />
                        <PermissionToggle
                          icon={<SettingsIcon className="w-4 h-4" />}
                          label="Manage settings"
                          checked={permissions.canManageSettings}
                          onChange={() => handleRolePermissionChange(role as UserRole, 'canManageSettings')}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'data' && (
              <div className={SETTINGS_SECTION_CLASS}>
                <h2 className="text-xl font-bold mb-6">Data Management</h2>
                <p className={`mb-6 text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>
                  Manage your application data, including clearing cache and resetting to defaults
                </p>

                {/* Allowed Users Section */}
                <div className="mb-6 rounded-lg border-2 border-chart-2/40 bg-chart-2/10 p-6">
                  <div className="flex items-start gap-4">
                    <Shield className="w-8 h-8 text-chart-2 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2">Allowed Users Access Control</h3>
                      <p className="mb-4 text-sm text-foreground/85">
                        View and manage users who are authorized to access the system. Only users listed as project team members or stakeholders can login with Google Authentication.
                      </p>
                      <Button
                        onClick={() => navigate('/app/allowed-users')}
                        className="bg-chart-2/10 hover:bg-chart-2/10"
                      >
                        <Shield className="w-4 h-4 mr-2" />
                        View Allowed Users
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Refresh Data Section */}
                <div className="mb-6 rounded-lg border-2 border-primary/40 bg-primary/10 p-6">
                  <div className="flex items-start gap-4">
                    <RefreshCw className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2">Refresh Application Data</h3>
                      <p className="mb-4 text-sm text-foreground/85">
                        If you don't see the latest projects (ProductionIzing ProjectFlow, 3D House Designer, Real Estate Analyzer), 
                        click the button below to clear cache and reload with the latest sample data.
                      </p>
                      <Button
                        onClick={() => {
                          if (confirm('This will clear all local data and reload with fresh sample data. Continue?')) {
                            localStorage.clear();
                            window.location.reload();
                          }
                        }}
                        className="bg-primary/10 hover:bg-primary/10"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh Data
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Clear All Data Section */}
                <div className="rounded-lg border-2 border-destructive/40 bg-destructive/10 p-6">
                  <div className="flex items-start gap-4">
                    <Trash2 className="w-8 h-8 text-destructive flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <h3 className="mb-2 text-lg font-semibold text-destructive">Reset to Factory Defaults</h3>
                      <p className="mb-4 text-sm text-destructive">
                        ⚠️ <strong>Warning:</strong> This will permanently delete all your projects, tasks, and settings. 
                        This action cannot be undone.
                      </p>
                      <Button
                        onClick={() => {
                          if (confirm('⚠️ This will DELETE ALL DATA and cannot be undone. Are you absolutely sure?')) {
                            if (confirm('Final confirmation: Delete everything and start fresh?')) {
                              localStorage.clear();
                              window.location.reload();
                            }
                          }
                        }}
                        variant="outline"
                        className="border-destructive/40 text-destructive hover:bg-destructive/10 dark:hover:bg-destructive/10"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Reset All Data
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Data Statistics */}
                <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className={`${WIDGET_SUBTLE_PANEL_CLASS} p-4`}>
                    <div className="text-2xl font-bold text-primary">{settings.projects.length}</div>
                    <div className={`text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>Projects</div>
                  </div>
                  <div className={`${WIDGET_SUBTLE_PANEL_CLASS} p-4`}>
                    <div className="text-2xl font-bold text-chart-2">{settings.users.length}</div>
                    <div className={`text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>Users</div>
                  </div>
                  <div className={`${WIDGET_SUBTLE_PANEL_CLASS} p-4`}>
                    <div className="text-2xl font-bold text-chart-4">{settings.organizations.length}</div>
                    <div className={`text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>Organizations</div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'language' && (
              <div className={SETTINGS_SECTION_CLASS}>
                <h2 className="mb-2 text-xl font-bold">Language & i18n</h2>
                <p className={`mb-6 text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>
                  Configure control terms for the selected language. Changes are saved immediately.
                </p>

                <div className={`${WIDGET_SUBTLE_PANEL_CLASS} mb-6 p-4`}>
                  <label className={`mb-2 block text-sm font-medium ${WIDGET_MUTED_TEXT_CLASS}`}>
                    Interface language
                  </label>
                  <select
                    value={preferences.language}
                    onChange={(event) => setLanguage(event.target.value as AppLanguage)}
                    className={APP_NATIVE_FIELD_CLASS}
                    title="Interface language"
                    aria-label="Interface language"
                  >
                    {LANGUAGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-3">
                  {I18N_CONTROL_TERM_KEYS.map((termKey) => (
                    <div key={termKey} className={`${WIDGET_SUBTLE_PANEL_CLASS} p-3`}>
                      <label className={`mb-1 block text-xs font-semibold uppercase tracking-wide ${WIDGET_MUTED_TEXT_CLASS}`}>
                        {TERM_LABELS[termKey] || termKey}
                      </label>
                      <input
                        value={languageTerms[termKey] || ''}
                        onChange={(event) => setTranslationTerm(termKey, event.target.value)}
                        className={APP_NATIVE_FIELD_CLASS}
                        title={`Translate ${TERM_LABELS[termKey] || termKey}`}
                        aria-label={`Translate ${TERM_LABELS[termKey] || termKey}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
      </div>
    </div>
  );
}

interface SettingToggleProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: () => void;
}

function SettingToggle({ label, description, checked, onChange }: SettingToggleProps) {
  return (
    <div className={`${WIDGET_SUBTLE_PANEL_CLASS} flex items-start justify-between p-4`}>
      <div className="flex-1">
        <div className="font-medium mb-1">{label}</div>
        <div className={`text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>{description}</div>
      </div>
      <label className="relative inline-flex items-center cursor-pointer ml-4">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="sr-only peer"
          title={label}
          aria-label={label}
        />
        <div className="h-6 w-11 rounded-full border border-border bg-muted peer transition-colors peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-ring/40 peer-checked:border-primary peer-checked:bg-primary after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border-border after:bg-background after:transition-all after:content-[''] peer-checked:after:translate-x-full peer-checked:after:border-primary-foreground"></div>
      </label>
    </div>
  );
}

interface PermissionToggleProps {
  icon: React.ReactNode;
  label: string;
  checked: boolean;
  onChange: () => void;
}

function PermissionToggle({ icon, label, checked, onChange }: PermissionToggleProps) {
  return (
    <div className={`${WIDGET_SUBTLE_PANEL_CLASS} flex items-center justify-between p-3`}>
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-sm">{label}</span>
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="w-4 h-4"
        title={label}
        aria-label={label}
      />
    </div>
  );
}